import { useEffect, useState } from 'react';
import {
  MessageBar,
  MessageBarBody,
  Spinner,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  BackLink,
  DietaryDots,
  NutritionTable,
  PriceText,
  QuantityStepper,
  StickyActionFooter,
} from '../components';
import { useCateringService } from '../app/CateringProvider';
import { getCartQuantity } from '../state/orderDraft';
import { useOrderDraft } from '../state/OrderDraftProvider';
import type { MenuItem } from '../types/catering';
import { ORDER_PATHS, useRouter } from '../router';
import placeholderImage from '../mocks/assets/menu/executive-breakfast.png';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    minHeight: '100%',
    paddingBottom: '5.5rem',
  },
  image: {
    width: '100%',
    height: '14.5rem',
    display: 'block',
    objectFit: 'cover',
  },
  titleRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: tokens.spacingHorizontalS,
    paddingInline: tokens.spacingHorizontalS,
  },
  description: {
    color: tokens.colorNeutralForeground2,
    lineHeight: tokens.lineHeightBase400,
    paddingInline: tokens.spacingHorizontalS,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  footer: {
    marginTop: 'auto',
  },
});

export default function ProductDetailPage() {
  const styles = useStyles();
  const service = useCateringService();
  const { state, dispatch } = useOrderDraft();
  const { params, navigate } = useRouter();
  const [item, setItem] = useState<MenuItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const itemId = Number(params.itemId);
  useEffect(() => {
    let isCurrent = true;
    if (!Number.isInteger(itemId) || itemId < 1) {
      setError('This menu item could not be identified.');
      setIsLoading(false);
      return () => {
        isCurrent = false;
      };
    }

    setIsLoading(true);
    setError(null);
    service
      .getMenuItem(itemId)
      .then((loadedItem) => {
        if (isCurrent) setItem(loadedItem);
      })
      .catch(() => {
        if (isCurrent) setError('We could not load this menu item.');
      })
      .finally(() => {
        if (isCurrent) setIsLoading(false);
      });

    return () => {
      isCurrent = false;
    };
  }, [service, itemId]);

  useEffect(() => {
    if (item && getCartQuantity(state, item.id) === 0) {
      dispatch({ type: 'addCartItem', itemId: item.id });
    }
  }, [dispatch, item, state]);

  if (isLoading) {
    return <Spinner label="Loading menu item" />;
  }

  if (error || !item) {
    return (
      <main className={styles.root}>
        <MessageBar intent="error">
          <MessageBarBody>{error ?? 'This menu item is no longer available.'}</MessageBarBody>
        </MessageBar>
        <BackLink onClick={() => navigate(ORDER_PATHS.menu)} />
      </main>
    );
  }

  const quantity = getCartQuantity(state, item.id);
  const handleQuantityChange = (nextQuantity: number) => {
    if (nextQuantity <= 0) {
      dispatch({ type: 'removeCartItem', itemId: item.id });
    } else if (quantity === 0) {
      dispatch({ type: 'addCartItem', itemId: item.id });
    } else if (nextQuantity > quantity) {
      dispatch({ type: 'incrementCartItem', itemId: item.id });
    } else if (nextQuantity < quantity) {
      dispatch({ type: 'decrementCartItem', itemId: item.id });
    }
  };

  return (
    <main className={styles.root}>
      <BackLink onClick={() => navigate(ORDER_PATHS.menu)} />
      <img className={styles.image} src={item.imageUrl ?? placeholderImage} alt="" />
      <div className={styles.titleRow}>
        <Text as="h1" size={500} weight="semibold">
          {item.name}
        </Text>
        <PriceText
          amount={item.priceInfo.grossUnitCosts.Delivery}
          size="large"
          weight="semibold"
          includeCurrency={false}
        />
      </div>
      <Text className={styles.description}>{item.description}</Text>
      <QuantityStepper
        value={quantity}
        min={Math.max(1, item.minimumWhenInOrder)}
        onChange={handleQuantityChange}
        label={`${item.name} ${item.isMeasured ? 'units' : 'portions'} per person`}
      />
      <section className={styles.section} aria-labelledby="dietary-heading">
        <Text as="h2" size={300} weight="semibold" id="dietary-heading">
          Allergens &amp; Dietary Info
        </Text>
        <DietaryDots allergens={item.allergens} />
      </section>
      <NutritionTable rows={item.nutritionFacts} />
      <div className={styles.footer}>
        <StickyActionFooter
          label="Continue"
          onClick={() => navigate(ORDER_PATHS.review)}
          disabled={state.lines.length === 0}
        />
      </div>
    </main>
  );
}
